import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { config } from '../../config';

export default function GererRechargesTechnicien() {
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [codeEnvoi, setCodeEnvoi] = useState('');
  const [montant, setMontant] = useState('');
  const [validating, setValidating] = useState(false);

  // Utilisation de la configuration centralisée
  const API_BASE = config.apiBase;
  const getToken = () => localStorage.getItem('token');

  const fetchDemandes = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/technicien/recharge/get_demandes_recharge.php`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setDemandes(data.demandes);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = (demande) => {
    setSelectedDemande(demande);
    setCodeEnvoi('');
    setMontant('');
    setShowModal(true);
  };

  const confirmValidation = async () => {
    if (!codeEnvoi.trim()) {
      toast.error('Code d\'envoi requis');
      return;
    }
    if (!montant || parseInt(montant) <= 0) {
      toast.error('Montant valide requis');
      return;
    }

    setValidating(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/technicien/recharge/valider_recharge.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          id_demande: selectedDemande.id_demande,
          code_envoi: codeEnvoi,
          montant: parseInt(montant)
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Recharge principale de ${data.montant} FCFA validée`);
        setShowModal(false);
        fetchDemandes();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Erreur lors de la validation');
    } finally {
      setValidating(false);
    }
  };

  useEffect(() => {
    fetchDemandes();
    const interval = setInterval(fetchDemandes, 10000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des demandes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">💰 Demandes de recharge principale</h1>
        <p className="text-gray-500">Validez les recharges du compte principal</p>
      </div>

      {demandes.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <div className="text-5xl mb-4">📭</div>
          <p className="text-gray-500">Aucune demande en attente</p>
        </div>
      ) : (
        <div className="space-y-4">
          {demandes.map(demande => (
            <div key={demande.id_demande} className="bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition border">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">💰 Compte principal</span>
                    <span className="text-xs text-gray-400">{new Date(demande.date_demande).toLocaleString()}</span>
                  </div>
                  <p className="font-semibold text-gray-800">{demande.etudiant_prenom} {demande.etudiant_nom}</p>
                  <p className="text-sm text-gray-500">{demande.etudiant_email}</p>
                  <p className="text-sm font-mono text-blue-600 mt-1">Code: {demande.code_envoi}</p>
                </div>
                <button onClick={() => handleValidate(demande)} className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition">✅ Valider</button>
              </div>
              {demande.recu_image_url && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs text-gray-500 mb-1">📷 Reçu:</p>
                  <img src={demande.recu_image_url} alt="Reçu" className="max-h-32 rounded cursor-pointer border" onClick={() => window.open(demande.recu_image_url)} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && selectedDemande && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b bg-gradient-to-r from-purple-600 to-pink-600 rounded-t-xl">
              <h3 className="text-lg font-bold text-white">Valider la recharge principale</h3>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-center">
                <p className="text-gray-600">Étudiant: <strong>{selectedDemande.etudiant_prenom} {selectedDemande.etudiant_nom}</strong></p>
              </div>
              {selectedDemande.recu_image_url && (
                <div className="border rounded-lg p-2">
                  <img src={selectedDemande.recu_image_url} alt="Reçu" className="max-h-40 mx-auto rounded cursor-pointer" onClick={() => window.open(selectedDemande.recu_image_url)} />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Code d'envoi reçu</label>
                <input type="text" value={codeEnvoi} onChange={(e) => setCodeEnvoi(e.target.value)} className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Montant à créditer (FCFA)</label>
                <input type="number" value={montant} onChange={(e) => setMontant(e.target.value)} className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div className="flex gap-3">
                <button onClick={confirmValidation} disabled={validating} className="flex-1 bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50">{validating ? 'Validation...' : '✅ Valider'}</button>
                <button onClick={() => setShowModal(false)} className="flex-1 bg-gray-200 py-2 rounded-lg hover:bg-gray-300">Annuler</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}